/**
 * `useMediaQuery` / `useIsMobile` — the adaptive-rendering primitive for
 * the mobile transition (umbrella #966, P0 / #967).
 *
 * Before this hook the SPA had NO media-query layout-switching anywhere
 * (the mobile-readiness audit found zero `matchMedia` usage). This is the
 * single piece of shared infrastructure the adaptive surfaces depend on:
 * a surface that needs a phone-specific component tree does
 *
 *     const isMobile = useIsMobile();
 *     return isMobile ? <MobileRosterView … /> : <RosterHeatmap … />;
 *
 * SSR-safe + concurrent-safe via `useSyncExternalStore` (matching the
 * `lib/persistentPanel` cross-tab subscription pattern). The desktop
 * branch is the no-`window` / no-`matchMedia` default, so nothing renders
 * a mobile tree unless a real viewport says so — **desktop output is
 * unchanged by construction**, which is the mobile transition's load-
 * bearing guarantee (strategy §6a).
 */
import { useCallback, useSyncExternalStore } from "react";

/**
 * The mobile/desktop switch. 768px is Tailwind's `md`; at or below 767px
 * is "mobile". Standardised here so the hook and the CSS breakpoints
 * agree on one number (mobile-transition strategy §6b — "standardise on
 * `md` (768px) as the switch").
 */
export const MOBILE_MAX_WIDTH_PX = 767;
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_MAX_WIDTH_PX}px)`;

function readMatch(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query).matches;
}

/**
 * Reactive media-query match. Returns `false` on the server / when
 * `matchMedia` is unavailable (the desktop-safe default).
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return () => undefined;
      }
      const mql = window.matchMedia(query);
      // `addEventListener('change')` is the modern API; older iOS Safari
      // (and some ward-iPad WebViews) only expose the deprecated
      // `addListener`. Support both so the subscription still fires there.
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
      }
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => readMatch(query), [query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/**
 * True on phone-width viewports (≤767px). The primary adaptive switch for
 * the mobile transition.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_MEDIA_QUERY);
}

/**
 * Tailwind's `lg` (1024px) — the width from which the lead's next-steps
 * panel handle mounts (#1920: the panel is an overlay drawer, so it costs
 * no page width and can start below `xl`).
 */
export const LG_MIN_WIDTH_PX = 1024;
export const BELOW_LG_MEDIA_QUERY = `(max-width: ${LG_MIN_WIDTH_PX - 1}px)`;

/**
 * True at `lg` (≥1024px) and wider. Phrased as the negation of a
 * max-width query on purpose: with no `matchMedia` (SSR, a bare test
 * environment) `useMediaQuery` reads `false`, so this reads `true` — the
 * desktop branch stays the default, the same guarantee `useIsMobile` keeps.
 */
export function useIsAtLeastLg(): boolean {
  return !useMediaQuery(BELOW_LG_MEDIA_QUERY);
}
